// ============================================================
// CHART IMAGE EXPORT
// ============================================================
// Renders a clean, presentation-ready PNG directly from the SAME
// { data, xKey, yKey, type } the chat chart already shows — never
// recomputed, never re-derived. Deliberately drawn on its own <canvas>
// (not a DOM screenshot of the app) so the export never contains chat
// controls, buttons, or app chrome, and uses a white background suitable
// for docs/slides/email rather than the app's dark theme.

const COLORS = ["#2563eb", "#8b5cf6", "#10b981", "#f59e0b", "#ec4899", "#06b6d4", "#ef4444", "#84cc16"];
const W = 1000;
const H = 640;
const PAD = 60;

function fmtValue(n) {
  if (n === null || n === undefined || isNaN(n)) return "N/A";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (abs >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

export function renderChartToCanvas({ data, xKey = "label", yKey = "value", type = "bar", title, subtitle, watermark = true }) {

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);

  // Title / subtitle
  let top = 40;

  if (title) {
    ctx.fillStyle = "#111827";
    ctx.font = "bold 24px Arial, sans-serif";
    ctx.fillText(title, PAD, top);
    top += 28;
  }

  if (subtitle) {
    ctx.fillStyle = "#6b7280";
    ctx.font = "15px Arial, sans-serif";
    ctx.fillText(subtitle, PAD, top);
    top += 20;
  }

  top += 20;

  const chartTop = top;
  const chartBottom = H - 70;
  const chartLeft = PAD;
  const chartRight = W - PAD;

  ctx.fillStyle = "#9ca3af";
  ctx.font = "12px Arial, sans-serif";
  ctx.fillText(
    watermark
      ? `Generated by InsightPulse AI  -  ${new Date().toLocaleDateString()}`
      : new Date().toLocaleDateString(),
    PAD,
    H - 24
  );

  if (!data?.length) {
    ctx.fillStyle = "#6b7280";
    ctx.font = "16px Arial, sans-serif";
    ctx.fillText("No data to visualize.", PAD, chartTop + 40);
    return canvas;
  }

  if (type === "pie") {
    drawPie(ctx, data, xKey, yKey, { chartLeft, chartRight, chartTop, chartBottom });
  } else if (type === "line") {
    drawLine(ctx, data, xKey, yKey, { chartLeft, chartRight, chartTop, chartBottom });
  } else {
    drawBar(ctx, data, xKey, yKey, { chartLeft, chartRight, chartTop, chartBottom });
  }

  return canvas;
}

function drawBar(ctx, data, xKey, yKey, { chartLeft, chartRight, chartTop, chartBottom }) {

  const max = Math.max(...data.map(d => d[yKey] || 0), 1);
  const w = chartRight - chartLeft;
  const h = chartBottom - chartTop;
  const gap = 16;
  const barW = (w - gap * (data.length - 1)) / data.length;

  ctx.strokeStyle = "#e5e7eb";
  ctx.beginPath();
  ctx.moveTo(chartLeft, chartBottom);
  ctx.lineTo(chartRight, chartBottom);
  ctx.stroke();

  data.forEach((d, i) => {
    const barH = Math.max((d[yKey] / max) * (h - 20), 2);
    const x = chartLeft + i * (barW + gap);
    const y = chartBottom - barH;

    ctx.fillStyle = COLORS[i % COLORS.length];
    ctx.fillRect(x, y, barW, barH);

    ctx.fillStyle = "#111827";
    ctx.font = "bold 13px Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(fmtValue(d[yKey]), x + barW / 2, y - 8);

    ctx.fillStyle = "#374151";
    ctx.font = "12px Arial, sans-serif";
    const label = String(d[xKey]);
    ctx.fillText(label.length > 14 ? label.slice(0, 13) + "…" : label, x + barW / 2, chartBottom + 20);
    ctx.textAlign = "left";
  });
}

function drawLine(ctx, data, xKey, yKey, { chartLeft, chartRight, chartTop, chartBottom }) {

  const values = data.map(d => d[yKey] ?? 0);
  const max = Math.max(...values);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const w = chartRight - chartLeft;
  const h = chartBottom - chartTop;
  const stepX = data.length > 1 ? w / (data.length - 1) : 0;

  ctx.strokeStyle = "#e5e7eb";
  ctx.beginPath();
  ctx.moveTo(chartLeft, chartBottom);
  ctx.lineTo(chartRight, chartBottom);
  ctx.stroke();

  const points = data.map((d, i) => ({
    x: chartLeft + i * stepX,
    y: chartBottom - ((d[yKey] - min) / range) * (h - 20),
  }));

  ctx.strokeStyle = "#2563eb";
  ctx.lineWidth = 3;
  ctx.beginPath();
  points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.stroke();
  ctx.lineWidth = 1;

  points.forEach((p, i) => {
    ctx.fillStyle = "#2563eb";
    ctx.beginPath();
    ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = "#111827";
    ctx.font = "bold 12px Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(fmtValue(data[i][yKey]), p.x, p.y - 12);

    ctx.fillStyle = "#374151";
    ctx.font = "12px Arial, sans-serif";
    ctx.fillText(String(data[i][xKey]), p.x, chartBottom + 20);
    ctx.textAlign = "left";
  });
}

function drawPie(ctx, data, xKey, yKey, { chartLeft, chartRight, chartTop, chartBottom }) {

  const total = data.reduce((s, d) => s + (d[yKey] || 0), 0) || 1;
  const cx = chartLeft + (chartRight - chartLeft) * 0.35;
  const cy = chartTop + (chartBottom - chartTop) / 2;
  const r = Math.min(chartBottom - chartTop, (chartRight - chartLeft) * 0.6) / 2 - 10;

  let angle = -Math.PI / 2;

  data.forEach((d, i) => {
    const slice = (d[yKey] / total) * Math.PI * 2;
    ctx.fillStyle = COLORS[i % COLORS.length];
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, r, angle, angle + slice);
    ctx.closePath();
    ctx.fill();
    angle += slice;
  });

  // Legend
  const legendX = chartLeft + (chartRight - chartLeft) * 0.68;
  let legendY = chartTop + 10;

  data.forEach((d, i) => {
    ctx.fillStyle = COLORS[i % COLORS.length];
    ctx.fillRect(legendX, legendY, 12, 12);

    ctx.fillStyle = "#111827";
    ctx.font = "13px Arial, sans-serif";
    const pct = ((d[yKey] / total) * 100).toFixed(1);
    ctx.fillText(`${d[xKey]}  (${pct}%)`, legendX + 18, legendY + 11);

    legendY += 22;
  });
}

export function canvasToBlob(canvas) {
  return new Promise(resolve => canvas.toBlob(resolve, "image/png"));
}

export async function downloadChartPng(canvas, filename = "chart.png") {
  const blob = await canvasToBlob(canvas);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function copyChartToClipboard(canvas) {
  const blob = await canvasToBlob(canvas);
  if (!navigator.clipboard || !window.ClipboardItem) {
    throw new Error("Clipboard image copy isn't supported in this browser.");
  }
  await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
}
